import apiService from "../services/apiService";

import { isEventStillActive } from "./dateUtils";
import { downloadPhotosAsZip, downloadSinglePhoto } from "./downloadUtils";

// Función para descargar QR code
export const handleQRDownload = async (qrCodeUrl, eventName) => {
  try {
    // Crear un canvas para convertir la imagen a blob
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Configurar el tamaño del canvas
      canvas.width = img.width;
      canvas.height = img.height;

      // Dibujar la imagen en el canvas
      ctx.drawImage(img, 0, 0);

      // Convertir a blob y descargar
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${eventName.replace(/\s+/g, "_")}_QR_Code.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/png");
    };

    img.onerror = () => {
      // Fallback: intentar descarga directa
      const link = document.createElement("a");
      link.href = qrCodeUrl;
      link.download = `${eventName.replace(/\s+/g, "_")}_QR_Code.png`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    img.src = qrCodeUrl;
  } catch (error) {
    console.error("Error downloading QR code:", error);
    // Fallback simple
    const link = document.createElement("a");
    link.href = qrCodeUrl;
    link.download = `${eventName.replace(/\s+/g, "_")}_QR_Code.png`;
    link.target = "_blank";
    link.click();
  }
};

export const handlePhotoClick = ({ photo, setModalPhoto, setIsModalOpen }) => {
  setModalPhoto(photo);
  setIsModalOpen(true);
};

export const handlePhotoSelect = ({
  photoId,
  selectedPhotos,
  setSelectedPhotos,
}) => {
  const newSelected = new Set(selectedPhotos);
  if (newSelected.has(photoId)) {
    newSelected.delete(photoId);
  } else {
    newSelected.add(photoId);
  }
  setSelectedPhotos(newSelected);
};

export const handleSelectAll = ({
  photos,
  selectedPhotos,
  setSelectedPhotos,
}) => {
  if (selectedPhotos.size === photos.length) {
    setSelectedPhotos(new Set());
  } else {
    setSelectedPhotos(new Set(photos.map((photo) => photo.id)));
  }
};

export const handleDownloadSelected = async ({
  selectedPhotos,
  photos,
  user,
  eventName,
}) => {
  try {
    const selectedPhotoObjects = photos.filter((photo) =>
      selectedPhotos.has(photo.id)
    );

    if (selectedPhotoObjects.length === 0) {
      throw new Error("No photos selected");
    }

    if (selectedPhotoObjects.length === 1) {
      // Always download single photo directly
      await downloadSinglePhoto(selectedPhotoObjects[0], eventName);
    } else {
      // Multiple photos: check if user is premium
      if (user?.isPremium) {
        // Premium users: download as zip
        const result = await downloadPhotosAsZip(
          selectedPhotoObjects,
          eventName
        );
        console.log(
          `Downloaded ${result.photoCount} photos as ${result.filename}`
        );
      } else {
        // Free users: download individually
        for (let i = 0; i < selectedPhotoObjects.length; i++) {
          const photo = selectedPhotoObjects[i];
          try {
            await downloadSinglePhoto(photo, eventName);
            // Add small delay between downloads to avoid overwhelming the browser
            if (i < selectedPhotoObjects.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          } catch (error) {
            console.error(`Error downloading photo ${photo.id}:`, error);
            // Continue with other photos even if one fails
          }
        }
      }
    }
  } catch (error) {
    console.error("Download failed:", error);
    throw error;
  }
};

export const handleDeleteSelected = ({
  selectedPhotos,
  photos,
  setPhotos,
  setSelectedPhotos,
  showConfirmModal,
  showSuccess,
  showError,
  selectedEventId,
  t,
  loadPhotosPage, // Nueva prop
  currentPage, // Nueva prop
}) => {
  const selectedCount = selectedPhotos.size;
  const selectedPhotosList = photos.filter((photo) =>
    selectedPhotos.has(photo.id)
  );

  showConfirmModal(
    t("dashboard.modalDeletePhoto.deletePhotos"),
    t("dashboard.modalDeletePhoto.deletePhotosSubtitle", {
      count: selectedCount,
    }),
    async () => {
      try {
        let deletedCount = 0;
        let failedCount = 0;

        // Delete each selected photo
        for (const photo of selectedPhotosList) {
          try {
            await apiService.deleteEventPhoto(photo.id, selectedEventId);
            deletedCount++;
          } catch (error) {
            console.error(`Error deleting photo ${photo.id}:`, error);
            failedCount++;
          }
        }

        // Check if current page becomes empty
        const remainingPhotosOnPage = photos.length - deletedCount;

        // If we're not on page 1 and this page becomes empty, go to previous page
        if (remainingPhotosOnPage === 0 && currentPage > 1) {
          await loadPhotosPage(currentPage - 1);
        } else {
          // Otherwise, reload the current page to get updated data
          await loadPhotosPage(currentPage);
        }

        // Clear selected photos
        setSelectedPhotos(new Set());

        // Show results
        if (deletedCount > 0) {
          showSuccess(
            t("dashboard.photoDelete.title", { count: deletedCount })
          );
        }
        if (failedCount > 0) {
          showError(
            t("dashboard.photoDeleteError.title", { count: failedCount })
          );
        }
      } catch (error) {
        console.error("Error in bulk delete:", error);
        showError("Error deleting photos");
      }
    }
  );
};

export const isEventActive = (event) => {
  return isEventStillActive(event.date, event.time);
};
